'use client';

/** Shared production loading UI; callers supply progress from their own pipeline. */
export function GenerationWaitPanelView({
  fileName, heading, uploadComplete, textComplete, planningComplete, firstCardComplete,
  sourceCount, facts = [], sampleFacts = false, batchReview = false,
}: {
  fileName: string;
  heading: string;
  uploadComplete: boolean;
  textComplete: boolean;
  planningComplete: boolean;
  firstCardComplete: boolean;
  sourceCount?: number | null;
  facts?: { slide_number: number; text: string }[];
  sampleFacts?: boolean;
  batchReview?: boolean;
}) {
  const sourceUnit = fileName.toLowerCase().endsWith('.pdf') ? 'Page' : 'Slide';
  const steps = [
    {
      label: 'Lecture secured',
      detail: uploadComplete ? 'Uploaded safely' : 'Uploading to secure storage',
      complete: uploadComplete,
    },
    {
      label: 'Reading slides',
      detail: sourceCount ? `${sourceCount} slides found` : 'Extracting lecture content',
      complete: textComplete,
    },
    { label: 'Selecting hard facts', detail: 'Using the whole lecture for context', complete: planningComplete },
    { label: batchReview ? 'Preparing first cards' : 'Preparing first card', detail: 'Grounding the prompt and answer', complete: firstCardComplete },
  ];
  const activeIndex = !uploadComplete ? 0 : !textComplete ? 1 : !planningComplete ? 2 : 3;

  return (
    <section className="overflow-hidden rounded-3xl border border-border bg-card shadow-sm">
      <div className="grid md:grid-cols-2">
      <div className="p-6 sm:p-8">
        <div className="flex items-start gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Building your queue</p>
            <h2 className="mt-2 font-serif text-2xl font-semibold" role="status" aria-live="polite">
              {heading}
            </h2>
            <p className="mt-1 max-w-xs truncate text-sm text-muted" title={fileName}>{fileName}</p>
          </div>
        </div>

        <ol className="mt-8 space-y-5">
          {steps.map((step, index) => {
            const active = index === activeIndex && !step.complete;
            return (
              <li key={step.label} className="flex gap-3">
                <span
                  aria-hidden="true"
                  className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-bold ${
                    step.complete
                      ? 'border-emerald-500 bg-emerald-500 text-white'
                      : active
                        ? 'border-transparent text-muted'
                        : 'border-border bg-surface text-muted'
                  }`}
                >
                  {step.complete ? '✓' : active ? (
                    <svg className="h-5 w-5 motion-safe:animate-spin" viewBox="0 0 24 24" fill="none" style={{ animationDuration: '1.2s' }}>
                      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" opacity="0.2" />
                      <path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  ) : index + 1}
                </span>
                <span>
                  <span className={`block text-sm font-semibold ${active || step.complete ? 'text-foreground' : 'text-muted'}`}>
                    {step.label}
                  </span>
                  <span className="mt-0.5 block text-xs leading-5 text-muted">{step.detail}</span>
                </span>
              </li>
            );
          })}
        </ol>

      </div>
        <aside className="min-w-0 border-t border-border p-6 sm:p-8 md:border-l md:border-t-0" aria-label={facts.length ? 'Selected facts' : 'About your deck'}>
          {facts.length > 0 ? <>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">{sampleFacts ? 'Demo content' : 'From your lecture'}</p>
          <h3 className="mt-2 font-serif text-2xl font-semibold">A few facts worth knowing</h3>
          <ol className="mt-6 space-y-5">
            {facts.map((item, index) => <li
              key={`${item.slide_number}-${item.text}`}
              className="border-l-2 border-border pl-4 motion-safe:animate-[fade-in_700ms_ease-out_both]"
              style={{ animationDelay: `${index * 350}ms` }}
            >
              <p className="text-xs text-muted">{sourceUnit} {item.slide_number}</p>
              <p className="mt-1 text-sm leading-6 text-foreground [overflow-wrap:anywhere]">{item.text}</p>
            </li>)}
          </ol>
          </> : <>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Behind your deck</p>
            <h3 className="mt-2 font-serif text-2xl font-semibold">What makes a useful card</h3>
            <ol className="mt-7 space-y-6">
              {[
                { title: 'One idea at a time', text: 'Focused prompts give you one specific idea to recall each time you review.' },
                { title: 'Grounded in your lecture', text: 'Selected facts are matched to the source text before they become cards.' },
                { title: 'Your final say', text: 'Keep what helps, edit the wording, and discard the rest. You decide what goes into your deck.' },
              ].map((item, index) => <li key={item.title} className="flex gap-4 motion-safe:animate-[fade-in_700ms_ease-out_both]" style={{ animationDelay: `${index * 350}ms` }}>
                <span aria-hidden="true" className="mt-0.5 text-xs tabular-nums text-muted">0{index + 1}</span>
                <div>
                  <p className="text-sm font-semibold text-foreground">{item.title}</p>
                  <p className="mt-1 text-sm leading-6 text-muted">{item.text}</p>
                </div>
              </li>)}
            </ol>
          </>}
        </aside>
      </div>
    </section>
  );
}
